import { FC, Fragment, useEffect, useState } from 'react';
import IResult from 'models/Result';
import IQuiz from 'models/Quiz';
import ResultItem from './ResultItem';
import { useAppSelector } from 'hooks/useStore';
import { selectResult } from 'store/player/playerSelectors';
import { selectQuiz } from 'store/quiz/quizSelectors';
import { buildUserName } from 'utilities/Utilities';
import { PAGE_SIZE, REFRESH_INTERVAL_MS } from './constants';
import classes from './styles.module.css';
import useCompactLayout from 'hooks/useCompactLayout';

type Props = {
  // When set, the page is driven externally (e.g. the compact leaderboard/QR slide
  // cycle in index.tsx) instead of rotating on its own internal timer.
  page?: number;
}

const ResultsList:FC<Props> = ({ page: controlledPage }) => {
  const isCompact = useCompactLayout();
  const isControlled = controlledPage !== undefined;

  const result:any = useAppSelector(selectResult);
  const storeQuiz = useAppSelector(selectQuiz);
  const quiz:IQuiz | null = storeQuiz || result?.quiz || null;
  const results:IResult[] = result?.results || [];
  const spotPrizeWinnerResult = result?.spotPrizeWinnerResult;

  const pageCount = Math.ceil(results.length / PAGE_SIZE) || 1;
  const isPaginated = pageCount > 1;
  const [internalPage, setInternalPage] = useState<number>(0);

  useEffect(() => {
    if (isControlled || !isPaginated) return;
    const rotationInterval = setInterval(() => {
      setInternalPage((currentPage) => (currentPage + 1) % pageCount);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(rotationInterval);
  }, [isControlled, isPaginated, pageCount]);

  useEffect(() => {
    if (isControlled) return;
    if (internalPage >= pageCount) setInternalPage(0);
  }, [isControlled, internalPage, pageCount]);

  if (!results.length) return null;

  const page = isControlled ? Math.min(controlledPage as number, pageCount - 1) : internalPage;
  const pageStart = page * PAGE_SIZE;
  const pageResults = results.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div
      className={`relative overflow-hidden shrink-0 rounded-3xl w-full shadow-primary-border ${isCompact ? 'p-4' : 'mb-4 pt-6 sm:mb-8'}`}
      style={{ backgroundColor: quiz?.publicLeaderboard?.backgroundColor ?? result?.quizGroup?.publicLeaderboard?.backgroundColor ?? 'rgba(0,0,0,0.6)' }}
    >
      <div key={page} className={isPaginated ? classes.pageFade : ''}>
        {pageResults.map((result, index) => (
          <ResultItem key={buildUserName(result)} result={result} resultIndex={pageStart + index} />
        ))}
      </div>
      {spotPrizeWinnerResult ? (
        <Fragment>
          <div className={`shadow-primary-border h-1 ${isCompact ? 'my-1' : 'my-4'}`} />
          <ResultItem key={buildUserName(spotPrizeWinnerResult)} result={spotPrizeWinnerResult} resultIndex={10} randomPrize />
        </Fragment>
      ) : null}
    </div>
  )
}

export default ResultsList;
